import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Avatar, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Divider, CircularProgress
} from '@mui/material';
import {
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  CurrencyRupee as RupeeIcon,
  Schedule as ScheduleIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as ClockIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const StudentDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStudentData();
  }, []);

  const fetchStudentData = async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data.data);

      // Fetch student's class timetable
      if (user?.studentProfile) {
        const ttRes = await api.get('/timetable/class-timetable', {
          params: {
            academicYearId: user.studentProfile.academicYearId || 1,
            classId: user.studentProfile.classId,
            sectionId: user.studentProfile.sectionId
          }
        });
        setTimetable(ttRes.data.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={48} sx={{ color: '#1a9e8f' }} />
        <Typography variant="body1" color="text.secondary">Loading your dashboard...</Typography>
      </Box>
    );
  }

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <Box>
      {/* Welcome Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #1a9e8f 0%, #158a7d 100%)', color: '#fff' }}>
        <CardContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Welcome back, {user?.firstName || 'Student'}!
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                Class: {stats?.className || '-'} • Division: {stats?.sectionName || '-'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={<CalendarIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                label={new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }}
              />
              <Chip
                icon={<ClockIcon sx={{ fontSize: 14, color: '#fff !important' }} />}
                label={new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    Class Teacher
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ my: 0.5 }}>
                    {stats?.classTeacherName || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Contact via staff room</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#1a9e8f15', color: '#1a9e8f', width: 48, height: 48 }}>
                  <PersonIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    My Attendance Rate
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ my: 0.5 }}>
                    {stats?.attendanceRate ? `${stats.attendanceRate}%` : '--'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Today Status: <Chip label={stats?.todayStatus || 'Not Marked'} size="small" variant="outlined" sx={{ height: 20 }} />
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#4caf5015', color: '#4caf50', width: 48, height: 48 }}>
                  <CheckIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase' }}>
                    Pending Fees Due
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ my: 0.5, color: (stats?.pendingFee || 0) > 0 ? '#e53935' : 'inherit' }}>
                    ₹{stats?.pendingFee?.toLocaleString('en-IN') || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Pay pending dues online</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#e5393515', color: '#e53935', width: 48, height: 48 }}>
                  <RupeeIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Timetable Section */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="primary" /> Class Weekly Timetable
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell><strong>Day</strong></TableCell>
                  {Array.from({ length: 8 }).map((_, idx) => (
                    <TableCell key={idx} align="center"><strong>Period {idx + 1}</strong></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {days.map(day => {
                  const daySchedule = timetable?.schedule?.[day] || [];
                  return (
                    <TableRow key={day} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{day}</TableCell>
                      {Array.from({ length: 8 }).map((_, idx) => {
                        const period = daySchedule[idx];
                        const subject = period?.subjectName || 'Free';
                        const teacher = period?.teacherName ? `(${period.teacherName})` : '';
                        return (
                          <TableCell key={idx} align="center" sx={{ fontSize: 12 }}>
                            <Typography variant="body2" sx={{ fontWeight: subject !== 'Free' ? 600 : 400 }}>
                              {subject}
                            </Typography>
                            {teacher && (
                              <Typography variant="caption" color="text.secondary">
                                {teacher}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StudentDashboard;
